import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Save, Loader2, Compass, PenLine, Camera } from 'lucide-react';

const API_URL = window.location.origin;

export const LegacySisters = ({ user }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const myCard = data?.members?.find(m => m.id === user?.id);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/auth/profile-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Your photo is up 📸');
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/profiles`);
      setData(res.data);
      const mine = res.data.members.find(m => m.id === user?.id);
      if (mine) setForm({ beyond_titles: mine.beyond_titles, becoming: mine.becoming, building: mine.building, how_to_hold: mine.how_to_hold });
    } catch (error) {
      console.error('Failed to load profiles:', error);
    }
  };

  useEffect(() => { fetchProfiles(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/legacy/profiles/me`, form);
      toast.success('Your profile is saved 🤍');
      setEditing(false);
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="h-64 bg-sand rounded-xl animate-pulse" />;

  const prompts = data.prompts || [];

  return (
    <div className="space-y-6" data-testid="legacy-sisters">
      <Card className="card-base border-terracotta/20 bg-gradient-to-br from-terracotta/5 to-transparent">
        <CardContent className="p-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-terracotta" />
              <p className="text-xs uppercase tracking-widest text-terracotta">The Sisters</p>
            </div>
            <p className="font-heading text-xl text-forest">Who are you beyond your titles?</p>
            <p className="text-sm text-gray-500 mt-1">Know the women you're walking with — not their résumés, their hearts.</p>
          </div>
          <Button size="sm" onClick={() => setEditing(!editing)} className="bg-forest hover:bg-forest/90 text-white" data-testid="sisters-edit-profile-btn">
            <PenLine className="h-4 w-4 mr-2" /> {editing ? 'Close' : 'My Profile'}
          </Button>
        </CardContent>
      </Card>

      {editing && (
        <Card className="card-base border-forest/20">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={myCard?.profile_image || undefined} />
                <AvatarFallback className="bg-forest text-white text-xl">{user?.full_name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto} data-testid="sisters-photo-upload-btn">
                  {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                  {myCard?.profile_image ? 'Change Photo' : 'Add Your Photo'}
                </Button>
                <p className="text-xs text-gray-400 mt-1">So your sisters can see the face behind the words</p>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} data-testid="sisters-photo-input" />
              </div>
            </div>
            {prompts.map(p => (
              <div key={p.field}>
                <p className="text-sm font-medium text-forest mb-1">{p.prompt}</p>
                <Textarea value={form[p.field] || ''} onChange={(e) => setForm({ ...form, [p.field]: e.target.value })}
                  rows={2} data-testid={`sisters-field-${p.field}`} />
              </div>
            ))}
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} className="bg-forest hover:bg-forest/90 text-white" data-testid="sisters-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save My Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.members.map(m => {
          const hasProfile = prompts.some(p => m[p.field]);
          return (
            <Card key={m.id} className="card-base" data-testid={`sister-card-${m.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => m.profile_image && setPhotoPreview({ src: m.profile_image, name: m.full_name })}
                    className={m.profile_image ? 'cursor-zoom-in rounded-full focus:outline-none focus:ring-2 focus:ring-terracotta/50' : 'cursor-default'}
                    data-testid={`sister-avatar-${m.id}`}>
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={m.profile_image || undefined} />
                      <AvatarFallback className="bg-forest text-white">{m.full_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                  </button>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-forest truncate">{m.full_name}</p>
                    {m.project_name && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 truncate">
                        <Compass className="h-3 w-3 text-terracotta flex-shrink-0" /> Building: {m.project_name}
                      </p>
                    )}
                  </div>
                </div>
                {hasProfile ? (
                  <div className="mt-4 space-y-3">
                    {prompts.map(p => m[p.field] && (
                      <div key={p.field}>
                        <p className="text-xs uppercase tracking-wide text-terracotta/80">{p.prompt}</p>
                        <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-line">{m[p.field]}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic mt-4">
                    {m.id === user?.id ? 'Your circle is waiting to know you — tap My Profile above.' : 'Her story is still being written here.'}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Photo lightbox */}
      <Dialog open={!!photoPreview} onOpenChange={(open) => !open && setPhotoPreview(null)}>
        <DialogContent className="max-w-xl p-2 bg-forest border-0" data-testid="sister-photo-lightbox">
          {photoPreview && (
            <div>
              <img src={photoPreview.src} alt={photoPreview.name} className="w-full max-h-[75vh] object-contain rounded-lg" />
              <p className="text-center text-white/80 text-sm py-2 font-heading">{photoPreview.name}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LegacySisters;
